import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  FirstSync,
  StaysSyncError,
  SyncAmbiguousAs,
  SyncFeed,
  SyncProvider,
  connectFeed,
  updateFeed,
} from '../../../../api/stays-sync-client';
import { AppButton } from '../../../button/app-button';
import { Field, FieldError, FieldHint, Input, Label } from '../../../form-kit';
import { FormModal } from '../../../modal/form-modal';
import { SegmentedControl } from '../../../segmented-control/segmented-control';
import { Shake } from '../../../../motion';
import toast from '../../../../utils/toast';
import {
  PROVIDERS,
  asksAmbiguous,
  connectedToast,
  feedTitle,
  importHint,
  linkError,
  providerName,
} from '../../../../utils/stays-sync';
import { Check, FormStack, Muted, ReadOnly } from './sync.styled';

/**
 * CONNECT A BOOKING SITE CALENDAR / ONE CALENDAR'S SETTINGS (plan S5,
 * screens §1). One FormModal, two modes:
 *   - create: the booking site, the link, a name, how closed periods read
 *     (Booking.com and Other only), "Add new stays without asking". "Test and
 *     connect" — the server fetches the link first and stores nothing if it
 *     fails; its refusal lands under the link field in words.
 *   - settings: the site is read-only text; the link shows only its hint and
 *     "Change link" opens an empty field (the old link is never sent here);
 *     plus "Pause this calendar". Only changed fields are sent, with the
 *     version the row was read at.
 * useState + a ref submit guard (no form library); Shake on a refused try.
 */

const AMBIGUOUS: ReadonlyArray<{ value: SyncAmbiguousAs; label: string }> = [
  { value: 'ask', label: 'Ask me each time' },
  { value: 'reservation', label: 'Stays' },
  { value: 'block', label: 'Blocked dates' },
];

interface FormState {
  provider: SyncProvider;
  url: string;
  label: string;
  ambiguous_as: SyncAmbiguousAs;
  auto_approve: boolean;
  paused: boolean;
}

export interface ConnectFeedModalProps {
  open: boolean;
  mode: 'create' | 'settings';
  listingId: string;
  /** settings: the calendar being edited. */
  feed?: SyncFeed | null;
  /** create: providers with a live calendar on this listing ('other' never counts). */
  taken: SyncProvider[];
  onClose: () => void;
  onConnected: (feed: SyncFeed, firstSync: FirstSync) => void;
  onSaved: (feed: SyncFeed) => void;
}

const initialOf = (mode: 'create' | 'settings', feed: SyncFeed | null | undefined, taken: SyncProvider[]): FormState => {
  if (mode === 'settings' && feed) {
    return {
      provider: feed.provider,
      url: '',
      label: feed.label || '',
      ambiguous_as: feed.ambiguous_as,
      auto_approve: feed.auto_approve,
      paused: feed.paused,
    };
  }
  const first = PROVIDERS.find(p => p.key === 'other' || !taken.includes(p.key));

  return {
    provider: first ? first.key : 'other',
    url: '',
    label: '',
    ambiguous_as: 'ask',
    auto_approve: false,
    paused: false,
  };
};

export const ConnectFeedModal: React.FC<ConnectFeedModalProps> = ({
  open,
  mode,
  listingId,
  feed = null,
  taken,
  onClose,
  onConnected,
  onSaved,
}) => {
  const uid = useId();
  const formId = `${uid}-form`;
  const [form, setForm] = useState<FormState>(() => initialOf(mode, feed, taken));
  const [initial, setInitial] = useState<FormState>(form);
  const [changeLink, setChangeLink] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [refusal, setRefusal] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [saving, setSaving] = useState(false);
  const busy = useRef(false);

  useEffect(() => {
    if (!open) return;
    const f = initialOf(mode, feed, taken);
    setForm(f);
    setInitial(f);
    setChangeLink(false);
    setErrors({});
    setRefusal(null);
    setAttempt(0);
    // `taken` is read on open only.
  }, [open, mode, feed]);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(initial), [form, initial]);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm(prev => ({ ...prev, [k]: v }));
  const title = mode === 'create' ? 'Connect a booking site calendar' : `${feed ? feedTitle(feed) : 'Calendar'} settings`;
  const needsUrl = mode === 'create' || changeLink;

  const refuse = (err: unknown) => {
    const x = err as StaysSyncError;
    setAttempt(a => a + 1);
    if (x && x.status === 400 && Object.keys(x.fields || {}).length) {
      setErrors(x.fields);

      return;
    }
    const extra = (x && x.extra) || {};
    const onLink = linkError(x && x.code, {
      reason: x && x.reason,
      httpStatus: typeof extra.http_status === 'number' ? extra.http_status : null,
      expected: typeof extra.expected === 'string' ? extra.expected : null,
    });
    if (onLink) {
      setErrors({ url: onLink });

      return;
    }
    if (x && x.code === 'FEED_PROVIDER_EXISTS') {
      setErrors({ provider: `${providerName(form.provider)} is already connected to this listing.` });

      return;
    }
    if (x && x.code === 'FEED_CHANGED') {
      setRefusal('Someone else changed this calendar. Close this window and open the settings again.');

      return;
    }
    setRefusal(x && x.status > 0 && x.message ? x.message : 'The calendar could not be saved. Please try again.');
  };

  const submit = async () => {
    if (busy.current) return;
    const local: Record<string, string> = {};
    if (needsUrl && !form.url.trim()) local.url = 'Paste the calendar link.';
    if (form.label.trim().length > 40) local.label = 'A name can be up to 40 characters.';
    setErrors(local);
    setRefusal(null);
    if (Object.keys(local).length) {
      setAttempt(a => a + 1);

      return;
    }
    busy.current = true;
    setSaving(true);
    try {
      if (mode === 'create') {
        const out = await connectFeed({
          listing_id: listingId,
          provider: form.provider,
          url: form.url.trim(),
          ...(form.label.trim() ? { label: form.label.trim() } : {}),
          auto_approve: form.auto_approve,
          ...(asksAmbiguous(form.provider) ? { ambiguous_as: form.ambiguous_as } : {}),
        });
        toast.success(connectedToast(out.feed, out.first_sync));
        onConnected(out.feed, out.first_sync);
      } else if (feed) {
        const patch: Parameters<typeof updateFeed>[1] = { version: feed.version };
        if (form.label.trim() !== (feed.label || '')) patch.label = form.label.trim() || null;
        if (form.paused !== feed.paused) patch.paused = form.paused;
        if (form.auto_approve !== feed.auto_approve) patch.auto_approve = form.auto_approve;
        if (asksAmbiguous(feed.provider) && form.ambiguous_as !== feed.ambiguous_as) patch.ambiguous_as = form.ambiguous_as;
        if (changeLink) patch.url = form.url.trim();
        const out = await updateFeed(feed.id, patch);
        toast.success(`${feedTitle(out.feed)} settings saved`);
        onSaved(out.feed);
      }
    } catch (err) {
      refuse(err);
    } finally {
      busy.current = false;
      setSaving(false);
    }
  };

  const providerOptions = PROVIDERS.map(p => ({
    value: p.key,
    label: p.name,
    disabled: p.key !== 'other' && taken.includes(p.key),
  }));
  const takenNames = PROVIDERS.filter(p => p.key !== 'other' && taken.includes(p.key)).map(p => p.name);

  const footer = (
    <>
      <AppButton variant="secondary" type="button" disabled={saving} onClick={onClose}>
        Cancel
      </AppButton>
      <AppButton type="submit" form={formId} loading={saving} loadingLabel={mode === 'create' ? 'Checking the link…' : 'Saving…'}>
        {mode === 'create' ? 'Test and connect' : 'Save'}
      </AppButton>
    </>
  );

  return (
    <FormModal
      open={open}
      title={title}
      onClose={onClose}
      isDirty={dirty}
      saving={saving}
      discardMessage="What you entered has not been saved. Close anyway and lose it?"
      footer={footer}
    >
      <Shake active={attempt > 0 && (Object.keys(errors).length > 0 || !!refusal)} attempt={attempt}>
        <FormStack
          id={formId}
          noValidate
          aria-label={title}
          onSubmit={event => {
            event.preventDefault();
            void submit();
          }}
        >
          <Field>
            <Label as="span" id={`${uid}-site`}>
              Booking site
            </Label>
            {mode === 'create' ? (
              <>
                <SegmentedControl
                  label="Booking site"
                  options={providerOptions}
                  value={form.provider}
                  onChange={v => set('provider', v)}
                />
                {takenNames.length ? (
                  <FieldHint id={`${uid}-taken`}>{`Already connected: ${takenNames.join(', ')}`}</FieldHint>
                ) : null}
              </>
            ) : (
              <ReadOnly aria-labelledby={`${uid}-site`}>{providerName(form.provider)}</ReadOnly>
            )}
            <FieldError message={errors.provider} />
          </Field>

          <Field>
            <Label htmlFor={`${uid}-url`}>Calendar link</Label>
            {needsUrl ? (
              <Input
                id={`${uid}-url`}
                type="url"
                inputMode="url"
                autoComplete="off"
                spellCheck={false}
                value={form.url}
                maxLength={2048}
                hasError={!!errors.url}
                aria-invalid={!!errors.url}
                aria-describedby={`${uid}-url-hint`}
                onChange={e => set('url', e.target.value)}
              />
            ) : (
              <ReadOnly id={`${uid}-url`}>{feed ? feed.url_hint : ''}</ReadOnly>
            )}
            <FieldHint id={`${uid}-url-hint`}>{importHint(form.provider)}</FieldHint>
            {mode === 'settings' && !changeLink ? (
              <div>
                <AppButton variant="ghost" size="compact" type="button" onClick={() => setChangeLink(true)}>
                  Change link
                </AppButton>
              </div>
            ) : null}
            <FieldError message={errors.url} />
          </Field>

          <Field>
            <Label htmlFor={`${uid}-label`}>Name (optional)</Label>
            <Input
              id={`${uid}-label`}
              value={form.label}
              maxLength={40}
              hasError={!!errors.label}
              onChange={e => set('label', e.target.value)}
            />
            <FieldError message={errors.label} />
          </Field>

          {asksAmbiguous(form.provider) ? (
            <Field>
              <Label as="span">Closed periods from this calendar are</Label>
              <SegmentedControl
                label="Closed periods from this calendar are"
                options={AMBIGUOUS}
                value={form.ambiguous_as}
                onChange={v => set('ambiguous_as', v)}
              />
              <FieldHint>Booking.com marks a booked stay and a closed date the same way.</FieldHint>
              <FieldError message={errors.ambiguous_as} />
            </Field>
          ) : null}

          <Field>
            <Check htmlFor={`${uid}-auto`}>
              <input
                id={`${uid}-auto`}
                type="checkbox"
                checked={form.auto_approve}
                aria-describedby={`${uid}-auto-hint`}
                onChange={e => set('auto_approve', e.target.checked)}
              />
              <span>Add new stays without asking</span>
            </Check>
            <FieldHint id={`${uid}-auto-hint`}>
              Date changes, clashes, cancellations and missing stays still wait for you.
            </FieldHint>
          </Field>

          {mode === 'settings' ? (
            <Field>
              <Check htmlFor={`${uid}-paused`}>
                <input
                  id={`${uid}-paused`}
                  type="checkbox"
                  checked={form.paused}
                  aria-describedby={`${uid}-paused-hint`}
                  onChange={e => set('paused', e.target.checked)}
                />
                <span>Pause this calendar</span>
              </Check>
              <FieldHint id={`${uid}-paused-hint`}>Lobbero stops checking it. Nothing already imported changes.</FieldHint>
            </Field>
          ) : null}

          {refusal ? <Muted role="alert">{refusal}</Muted> : null}
        </FormStack>
      </Shake>
    </FormModal>
  );
};
